import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@components/common/Icon';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    maxWidth?: string;
    onPrev?: () => void;
    onNext?: () => void;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
}


const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-[680px]', onPrev, onNext, isPrevDisabled, isNextDisabled }: ModalProps) => {
    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const hasNav = onPrev || onNext;

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[1px] px-4"
            // onClick={onClose}
        >
            {/* Left arrow */}
            {hasNav && (
                <button
                    type="button"
                    onClick={onPrev}
                    disabled={isPrevDisabled}
                    title="Previous"
                    className="shrink-0 mr-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 border border-white/60 text-slate-700 shadow-lg hover:bg-white hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-150 backdrop-blur-sm"
                    style={{ zIndex: 101 }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            <div
                className={`relative w-full ${maxWidth} bg-white border border-[var(--neutral-color)] rounded-2xl flex flex-col max-h-[90vh]`}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 2xl:px-5 py-2 2xl:py-3 border-b border-[var(--neutral-color)]">
                    <h2 className="text-[14px] 2xl:text-[18px] font-medium text-[var(--secondary-color)] leading-tight">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex items-center justify-center cursor-pointer"
                        aria-label="Close"
                    >
                        <Icon name="modalClose" className='w-2 2xl:w-3 h-auto' alt="Close" />
                    </button>
                </div>

                {/* Scrollable body */}
                    {children}
            </div>

            {/* Right arrow */}
            {hasNav && (
                <button
                    type="button"
                    onClick={onNext}
                    disabled={isNextDisabled}
                    title="Next"
                    className="shrink-0 ml-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 border border-white/60 text-slate-700 shadow-lg hover:bg-white hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-150 backdrop-blur-sm"
                    style={{ zIndex: 101 }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}
        </div>,
        document.body,
    );
};

export default Modal;
